import { Formik, Form, Field } from "formik";
import { TextField, Button, MenuItem } from "@mui/material";
import * as yup from "yup";
import React, { Fragment, useState } from "react";
import MainCalculatorsList from "../MainCalculators";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { CalculatorsType } from "../ConstantsEN";
import { FirstAds, SecondAds,MobileUnderCalAds } from "../../Ads";
import rectangularimg from "../../../images/area-rectangle.gif";
import triangleimg from "../../../images/area-triangle.gif";
import trapezoidimg from "../../../images/area-trapezoid.gif";
import circleimg from "../../../images/area-circle.gif";
import sectorimg from "../../../images/area-sector.gif";
import ellipseimg from "../../../images/area-ellipse.gif";
import parallelogramimg from "../../../images/area-parallelogram.gif";
const TITLE = "Area Calculator";
const DESCRIPTION = "This calculator calculates the area of various common geometric shapes, including rectangle, triangle, parallelogram, circle, sector, and ellipse.";
const KEYWORDS = "Area calculator, geometric shapes, calculation, geometric shape, rectangle, triangle, parallelogram, circle, sector, ellipse";
const OG_URL = "https://do-calculate.com/calculator/en/areas";
const conversionMap = {
  centimeters: 0.01,
  inches: 0.0254,
  meters: 1,
  kilometers: 1000,
  feet: 0.3048,
  miles: 1609.34,
};
const rectangularSchema = yup.object().shape({
  length: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
  width: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
});
const initialRectangularValues = {
  length: "",
  width: "",
  lengthUnit: "meters",
  widthUnit: "meters",
};
const triangleSchema = yup.object().shape({
  edge1: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
  edge2: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
  edge3: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
});
const initialTriangleValues = {
  edge1: "",
  edge2: "",
  edge3: "",
  edge1Unit: "meters",
  edge2Unit: "meters",
  edge3Unit: "meters",
};
const trapezoidSchema = yup.object().shape({
  base1: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
  base2: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
  height: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
});
const initialTrapezoidValues = {
  base1: "",
  base2: "",
  height: "",
  base1Unit: "meters",
  base2Unit: "meters",
  heightUnit: "meters",
};
const circleSchema = yup.object().shape({
  radius: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
});
const initialCircleValues = {
  radius: "",
  radiusUnit: "meters",
};
const sectorSchema = yup.object().shape({
  radius: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
  angle: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(360, "Please enter values between 0 and 360")
    .min(0, "Please enter positive values between 0 and 360"),
});
const initialSectorValues = {
  radius: "",
  angle: "",
  radiusUnit: "meters",
};
const ellipseSchema = yup.object().shape({
  semiMajorAxes: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
  semiMinorAxes: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
});
const initialEllipseValues = {
  semiMajorAxes: "",
  semiMinorAxes: "",
  semiMajorAxesUnit: "meters",
  semiMinorAxesUnit: "meters",
};
const parallelogramSchema = yup.object().shape({
  base: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
  height: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "The number is too large")
    .min(0, "Make sure not to enter negative values"),
});
const initialParallelogramValues = {
  base: "",
  height: "",
  baseUnit: "meters",
  heightUnit: "meters",
};
const AreaCalculators = () => {
  const [rectangularArea, setRectangularArea] = useState("");
  const [convertedRectangularUnit, setConvertedRectangularUnit] =
    useState("meters");
  const onSubmitRectangular = (values) => {
    const { length, width, lengthUnit, widthUnit } = values;
    let convertedLength = length;
    let convertedWidth = width;
    let resultUnit = "";
    if (conversionMap[lengthUnit] >= conversionMap[widthUnit]) {
      resultUnit = lengthUnit;
    } else {
      resultUnit = widthUnit;
    }
    if (resultUnit !== lengthUnit) {
      convertedLength =
        (length * conversionMap[lengthUnit]) / conversionMap[resultUnit];
    }
    if (resultUnit !== widthUnit) {
      convertedWidth =
        (width * conversionMap[widthUnit]) / conversionMap[resultUnit];
    }
    const rectangularArea = convertedLength * convertedWidth;
    setRectangularArea(rectangularArea);
    setConvertedRectangularUnit(resultUnit);
  };
  const [triangleArea, setTriangleArea] = useState("");
  const [convertedTriangleUnit, setConvertedTriangleUnit] = useState("m");
  const [errorMessage, setErrorMessage] = useState("");
  const onSubmitTriangle = (values) => {
    const { edge1, edge2, edge3, edge1Unit, edge2Unit, edge3Unit } = values;
    const convertedEdges = [
      edge1 * conversionMap[edge1Unit],
      edge2 * conversionMap[edge2Unit],
      edge3 * conversionMap[edge3Unit],
    ];
    const maxUnitValue = Math.max(
      conversionMap[edge1Unit],
      conversionMap[edge2Unit],
      conversionMap[edge3Unit]
    );
    const resultUnit = Object.keys(conversionMap).find(
      (key) => conversionMap[key] === maxUnitValue
    );
    const convertedEdgesNormalized = convertedEdges.map(
      (edge) => edge / maxUnitValue
    );
    if (
      convertedEdgesNormalized[2] <
      convertedEdgesNormalized[0] + convertedEdgesNormalized[1]
    ) {
      const p =
        (convertedEdges[0] + convertedEdges[1] + convertedEdges[2]) /
        (2 * maxUnitValue);
      const triangleArea = Math.sqrt(
        p *
        (p - convertedEdgesNormalized[0]) *
        (p - convertedEdgesNormalized[1]) *
        (p - convertedEdgesNormalized[2])
      );
      const convertedArea = triangleArea;
      setTriangleArea(convertedArea);
      setConvertedTriangleUnit(resultUnit);
      setErrorMessage("");
    } else {
      setTriangleArea("");
      setConvertedTriangleUnit("");
      setErrorMessage("مجموع اي حافتين لايجب ان يكون اكبر من الثالثة");
    }
  };
  const [trapezoidArea, setTrapezoidArea] = useState("");
  const [convertedTrapezoidUnit, setConvertedTrapezoidUnit] =
    useState("meters");
  const onSubmitTrapezoid = (values) => {
    const { base1, base2, height, base1Unit, base2Unit, heightUnit } = values;
    let convertedBase1 = base1;
    let convertedBase2 = base2;
    let convertedHeight = height;
    let resultUnit = "";
    if (
      conversionMap[base1Unit] >= conversionMap[base2Unit] &&
      conversionMap[base1Unit] >= conversionMap[heightUnit]
    ) {
      resultUnit = base1Unit;
    } else if (
      conversionMap[base2Unit] >= conversionMap[base1Unit] &&
      conversionMap[base2Unit] >= conversionMap[heightUnit]
    ) {
      resultUnit = base2Unit;
    } else {
      resultUnit = heightUnit;
    }
    if (resultUnit !== base1Unit) {
      convertedBase1 =
        (base1 * conversionMap[base1Unit]) / conversionMap[resultUnit];
    }
    if (resultUnit !== base2Unit) {
      convertedBase2 =
        (base2 * conversionMap[base2Unit]) / conversionMap[resultUnit];
    }
    if (resultUnit !== heightUnit) {
      convertedHeight =
        (height * conversionMap[heightUnit]) / conversionMap[resultUnit];
    }
    const trapezoidArea =
      ((Number(convertedBase1) + Number(convertedBase2)) / 2) * convertedHeight;
    setTrapezoidArea(trapezoidArea);
    setConvertedTrapezoidUnit(resultUnit);
  };
  const [circleArea, setCircleArea] = useState("");
  const [convertedCircleUnit, setConvertedCircleUnit] = useState("meters");
  const onSubmitCircle = (values) => {
    const { radius, radiusUnit } = values;
    const circleArea = Math.PI * Math.pow(radius, 2);
    setCircleArea(circleArea);
    setConvertedCircleUnit(radiusUnit);
  };
  const [sectorArea, setSectorArea] = useState("");
  const [convertedSectorUnit, setConvertedSectorUnit] = useState("meters");
  const onSubmitSector = (values) => {
    const { radius, angle, radiusUnit } = values;
    const sectorArea = (angle / 360) * Math.PI * Math.pow(radius, 2);
    setSectorArea(sectorArea);
    setConvertedSectorUnit(radiusUnit);
  };
  const [ellipseArea, setEllipseArea] = useState("");
  const [convertedEllipseUnit, setConvertedEllipseUnit] = useState("meters");
  const onSubmitEllipse = (values) => {
    const {
      semiMajorAxes,
      semiMinorAxes,
      semiMajorAxesUnit,
      semiMinorAxesUnit,
    } = values;
    let convertedSemiMajorAxes = semiMajorAxes;
    let convertedSemiMinorAxes = semiMinorAxes;
    let resultUnit = "";
    if (conversionMap[semiMajorAxesUnit] >= conversionMap[semiMinorAxesUnit]) {
      resultUnit = semiMajorAxesUnit;
    } else {
      resultUnit = semiMinorAxesUnit;
    }
    if (resultUnit !== semiMajorAxesUnit) {
      convertedSemiMajorAxes =
        (semiMajorAxes * conversionMap[semiMajorAxesUnit]) /
        conversionMap[resultUnit];
    }
    if (resultUnit !== semiMinorAxesUnit) {
      convertedSemiMinorAxes =
        (semiMinorAxes * conversionMap[semiMinorAxesUnit]) /
        conversionMap[resultUnit];
    }
    const ellipseArea =
      Math.PI * convertedSemiMajorAxes * convertedSemiMinorAxes;
    setEllipseArea(ellipseArea);
    setConvertedEllipseUnit(resultUnit);
  };
  const [parallelogramArea, setParallelogramArea] = useState("");
  const [convertedParallelogramUnit, setConvertedParallelogramUnit] =
    useState("meters");
  const onSubmitParallelogram = (values) => {
    const { base, height, baseUnit, heightUnit } = values;
    let convertedBase = base;
    let convertedHeight = height;
    let resultUnit = "";
    if (conversionMap[baseUnit] >= conversionMap[heightUnit]) {
      resultUnit = baseUnit;
    } else {
      resultUnit = heightUnit;
    }
    if (resultUnit !== baseUnit) {
      convertedBase =
        (base * conversionMap[baseUnit]) / conversionMap[resultUnit];
    }
    if (resultUnit !== heightUnit) {
      convertedHeight =
        (height * conversionMap[heightUnit]) / conversionMap[resultUnit];
    }
    const parallelogramArea = convertedBase * convertedHeight;
    setParallelogramArea(parallelogramArea);
    setConvertedParallelogramUnit(resultUnit);
  };
  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
          >
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="row">
            <div>
              <p>
                Area is a quantity that describes the extent of a two-dimensional shape or surface. It can be visualized as the amount of paint needed to cover a surface and is the two-dimensional counterpart to the length of a curve and the volume of a three-dimensional mass. The standard unit for area in the International System of Units (SI) is the square meter, or m<sup>2</sup>. Below are equations for some common and simple shapes, along with examples of how to calculate the area of each.
              </p>
              <h2 style={{ fontSize: "17px" }}>
                The area of geometric shapes is calculated as follows:
              </h2>
              <ul>
                <li>Rectangle Area: To calculate the area of a rectangle, multiply the length by the width.</li>
                <li>
                  Area of a Triangle with Three Sides: Calculate it using Heron's Formula:
                  <br />
                  s(s-a)(s-b)(s-c)√
                  <br />
                  where s = (a+b+c) / 2
                </li>
                <li>
                  Trapezoid Area: Calculate the area of a trapezoid using the following formula:
                  (2/ (b1 + b2)) × h
                </li>
                <li>
                  Circle Area: To calculate the area of a circle, multiply π by the radius squared.
                </li>
                <li>
                  Sector Area: Calculate the area of a sector using the formula:
                  (angle/360) × π × radius squared
                </li>
                <li>
                  Ellipse Area: Calculate the area of an ellipse using the formula:
                  Area = πab
                </li>
                <li>
                  Parallelogram Area: Calculate the area of a parallelogram by multiplying the base by the height.
                </li>
              </ul>
              <b>
                You can also use the following calculator to calculate the area of geometric shapes:
              </b>
              <hr />
            </div>
             <MobileUnderCalAds/>
            <div className="col-sm-12 col-md-7 calculatorStyle">
              <Formik
                initialValues={initialRectangularValues}
                validationSchema={rectangularSchema}
                onSubmit={onSubmitRectangular}
              >
                {(formik) => (
                  <Form>
                    <div className="mb-3">
                      <h3
                        className="p-3 mb-2 bg-success text-white"
                        style={{ fontSize: "16px", fontWeight: "bold" }}
                      >
                        حساب مساحة المستطيل - حاسبة مساحة المستطيل
                      </h3>
                      <TextField
                        label="Length"
                        name="length"
                        value={formik.values.length}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.length && formik.errors.length}
                        helperText={
                          formik.touched.length && formik.errors.length
                        }
                        sx={{
                          width: "160px",
                          ml: "10px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor: "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <Field
                        as={TextField}
                        select
                        name="lengthUnit"
                        value={formik.values.lengthUnit}
                        onChange={formik.handleChange}
                        size="small"
                        style={{
                          backgroundColor: "#f9f9f9",
                          pending: "5px",
                        }}
                      >
                        {Object.keys(conversionMap).map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Field>
                    </div>
                    <div className="mb-3">
                      <TextField
                        label="Width"
                        name="width"
                        value={formik.values.width}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.width && formik.errors.width}
                        helperText={formik.touched.width && formik.errors.width}
                        sx={{
                          width: "160px",
                          ml: "10px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor: "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <Field
                        as={TextField}
                        select
                        name="widthUnit"
                        value={formik.values.widthUnit}
                        onChange={formik.handleChange}
                        size="small"
                        style={{
                          backgroundColor: "#f9f9f9",
                          pending: "5px",
                        }}
                      >
                        {Object.keys(conversionMap).map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Field>
                    </div>
                    <div className="mb-3">
                      <Button
                        type="submit"
                        disabled={!formik.isValid}
                        color="success"
                        sx={{
                          backgroundColor: "rgb(25,135,84)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                      >
                        إحسب
                      </Button>
                      <Button
                        disabled={!formik.isValid}
                        sx={{
                          backgroundColor: "rgb(108,117,125)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                        onClick={() => {
                          setRectangularArea(null);
                          formik.resetForm();
                        }}
                      >
                        امسح النتيجة
                      </Button>
                    </div>
                    {rectangularArea && rectangularArea > 0 ? (
                      <Fragment>
                        <p className="p-3 mb-2 bg-success text-white">
                          النتيجة : مساحة المستطيل هو : {rectangularArea}{" "}
                          {convertedRectangularUnit}
                          <sup>2</sup>
                        </p>
                      </Fragment>
                    ) : (
                      ""
                    )}
                  </Form>
                )}
              </Formik>
            </div>
            <div className="col-sm-12 col-md-4 mt-md-5 mt-3 text-center">
              <img alt="حساب مساحة المستطيل - حاسبة مساحة المستطيل" src={rectangularimg} />
            </div>
          </div>
          <hr />
                    <div className="row">
            <div className="col-sm-12 col-md-7 calculatorStyle">
              <Formik
                initialValues={initialTriangleValues}
                validationSchema={triangleSchema}
                onSubmit={onSubmitTriangle}
              >
                {(formik) => (
                  <Form>
                    <div className="mb-3">
                      <h3
                        className="p-3 mb-2 bg-success text-white"
                        style={{ fontSize: "16px", fontWeight: "bold" }}
                      >
                        Triangle Area Calculator - Calculate the Area of a Triangle
                      </h3>
                      <TextField
                        label="Edge 1"
                        name="edge1"
                        value={formik.values.edge1}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.edge1 && formik.errors.edge1}
                        helperText={formik.touched.edge1 && formik.errors.edge1}
                        sx={{
                          width: "160px",
                          ml: "10px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor: "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <Field
                        as={TextField}
                        select
                        name="edge1Unit"
                        value={formik.values.edge1Unit}
                        onChange={formik.handleChange}
                        size="small"
                        style={{
                          backgroundColor: "#f9f9f9",
                          pending: "5px",
                        }}
                      >
                        {Object.keys(conversionMap).map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Field>
                    </div>
                    <div className="mb-3">
                      <TextField
                        label="Edge 2"
                        name="edge2"
                        value={formik.values.edge2}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.edge2 && formik.errors.edge2}
                        helperText={formik.touched.edge2 && formik.errors.edge2}
                        sx={{
                          width: "160px",
                          ml: "10px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor: "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <Field
                        as={TextField}
                        select
                        name="edge2Unit"
                        value={formik.values.edge2Unit}
                        onChange={formik.handleChange}
                        size="small"
                        style={{
                          backgroundColor: "#f9f9f9",
                          pending: "5px",
                        }}
                      >
                        {Object.keys(conversionMap).map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Field>
                    </div>
                    <div className="mb-3">
                      <TextField
                        label="Edge 3"
                        name="edge3"
                        value={formik.values.edge3}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.edge3 && formik.errors.edge3}
                        helperText={formik.touched.edge3 && formik.errors.edge3}
                        sx={{
                          width: "160px",
                          ml: "10px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor: "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <Field
                        as={TextField}
                        select
                        name="edge3Unit"
                        value={formik.values.edge3Unit}
                        onChange={formik.handleChange}
                        size="small"
                        style={{
                          backgroundColor: "#f9f9f9",
                          pending: "5px",
                        }}
                      >
                        {Object.keys(conversionMap).map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Field>
                    </div>
                    <div className="mb-3">
                      <Button
                        type="submit"
                        disabled={!formik.isValid}
                        color="success"
                        sx={{
                          backgroundColor: "rgb(25,135,84)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                      >
                        إحسب
                      </Button>
                      <Button
                        disabled={!formik.isValid}
                        sx={{
                          backgroundColor: "rgb(108,117,125)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                        onClick={() => {
                          setTriangleArea(null);
                          formik.resetForm();
                          setErrorMessage("");
                        }}
                      >
                        امسح النتيجة
                      </Button>
                    </div>
                    {errorMessage && (
                      <p className="p-3 mb-2 bg-danger text-white">
                        {errorMessage}
                      </p>
                    )}
                    {triangleArea && triangleArea > 0 ? (
                      <Fragment>
                        <p className="p-3 mb-2 bg-success text-white">
                          The result: The area of the triangle is: {triangleArea}{" "}
                          {convertedTriangleUnit}
                          <sup>2</sup>
                        </p>
                      </Fragment>
                    ) : (
                      ""
                    )}
                  </Form>
                )}
              </Formik>
            </div>
            <div className="col-sm-12 col-md-4 mt-md-5 mt-3 text-center">
              <img alt="Area Calculator" src={triangleimg} />
            </div>
          </div>
          <hr />
                    <div className="row">
            <div className="col-sm-12 col-md-7 calculatorStyle">
              <Formik
                initialValues={initialTrapezoidValues}
                validationSchema={trapezoidSchema}
                onSubmit={onSubmitTrapezoid}
              >
                {(formik) => (
                  <Form>
                    <div className="mb-3">
                      <h3
                        className="p-3 mb-2 bg-success text-white"
                        style={{ fontSize: "16px", fontWeight: "bold" }}
                      >
                        Calculating the Trapezoid Area - Trapezoid Area Calculator
                      </h3>
                      <TextField
                        label="Base 1"
                        name="base1"
                        value={formik.values.base1}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.base1 && formik.errors.base1}
                        helperText={formik.touched.base1 && formik.errors.base1}
                        sx={{
                          width: "160px",
                          ml: "10px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor: "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <Field
                        as={TextField}
                        select
                        name="base1Unit"
                        value={formik.values.base1Unit}
                        onChange={formik.handleChange}
                        size="small"
                        style={{
                          backgroundColor: "#f9f9f9",
                          pending: "5px",
                        }}
                      >
                        {Object.keys(conversionMap).map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Field>
                    </div>
                    <div className="mb-3">
                      <TextField
                        label="Base 2"
                        name="base2"
                        value={formik.values.base2}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.base2 && formik.errors.base2}
                        helperText={formik.touched.base2 && formik.errors.base2}
                        sx={{
                          width: "160px",
                          ml: "10px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor: "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <Field
                        as={TextField}
                        select
                        name="base2Unit"
                        value={formik.values.base2Unit}
                        onChange={formik.handleChange}
                        size="small"
                        style={{
                          backgroundColor: "#f9f9f9",
                          pending: "5px",
                        }}
                      >
                        {Object.keys(conversionMap).map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Field>
                    </div>
                    <div className="mb-3">
                      <TextField
                        label="Height"
                        name="height"
                        value={formik.values.height}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.height && formik.errors.height}
                        helperText={
                          formik.touched.height && formik.errors.height
                        }
                        sx={{
                          width: "160px",
                          ml: "10px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor: "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <Field
                        as={TextField}
                        select
                        name="heightUnit"
                        value={formik.values.heightUnit}
                        onChange={formik.handleChange}
                        size="small"
                        style={{
                          backgroundColor: "#f9f9f9",
                          pending: "5px",
                        }}
                      >
                        {Object.keys(conversionMap).map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Field>
                    </div>
                    <div className="mb-3">
                      <Button
                        type="submit"
                        disabled={!formik.isValid}
                        color="success"
                        sx={{
                          backgroundColor: "rgb(25,135,84)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                      >
                        Calculate
                      </Button>
                      <Button
                        disabled={!formik.isValid}
                        sx={{
                          backgroundColor: "rgb(108,117,125)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                        onClick={() => {
                          setTrapezoidArea(null);
                          formik.resetForm();
                        }}
                      >
                        Clear
                      </Button>
                    </div>
                    {trapezoidArea && trapezoidArea > 0 ? (
                      <Fragment>
                        <p className="p-3 mb-2 bg-success text-white">
                          The result: The area of the trapezoid is: {" "}
                          {trapezoidArea} {convertedTrapezoidUnit}
                          <sup>2</sup>
                        </p>
                      </Fragment>
                    ) : (
                      ""
                    )}
                  </Form>
                )}
              </Formik>
            </div>
            <div className="col-sm-12 col-md-4 mt-md-5 mt-3 text-center">
              <img alt="Calculating the Trapezoid Area - Trapezoid Area Calculator" src={trapezoidimg} />
            </div>
          </div>
          <hr />
                    <div className="row">
            <div className="col-sm-12 col-md-7 calculatorStyle">
              <Formik
                initialValues={initialCircleValues}
                validationSchema={circleSchema}
                onSubmit={onSubmitCircle}
              >
                {(formik) => (
                  <Form>
                    <div className="mb-3">
                      <h3
                        className="p-3 mb-2 bg-success text-white"
                        style={{ fontSize: "16px", fontWeight: "bold" }}
                      >
                        Calculating the Circle Area - Circle Area Calculator
                      </h3>
                      <TextField
                        label="Radius"
                        name="radius"
                        value={formik.values.radius}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.radius && formik.errors.radius}
                        helperText={
                          formik.touched.radius && formik.errors.radius
                        }
                        sx={{
                          width: "160px",
                          ml: "10px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor: "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <Field
                        as={TextField}
                        select
                        name="radiusUnit"
                        value={formik.values.radiusUnit}
                        onChange={formik.handleChange}
                        size="small"
                        style={{
                          backgroundColor: "#f9f9f9",
                          pending: "5px",
                        }}
                      >
                        {Object.keys(conversionMap).map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Field>
                    </div>
                    <div className="mb-3">
                      <Button
                        type="submit"
                        disabled={!formik.isValid}
                        color="success"
                        sx={{
                          backgroundColor: "rgb(25,135,84)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                      >
                        Calculate
                      </Button>
                      <Button
                        disabled={!formik.isValid}
                        sx={{
                          backgroundColor: "rgb(108,117,125)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                        onClick={() => {
                          setCircleArea(null);
                          formik.resetForm();
                        }}
                      >
                        Clear
                      </Button>
                    </div>
                    {circleArea && circleArea > 0 ? (
                      <Fragment>
                        <p className="p-3 mb-2 bg-success text-white">
                          The result: The area of the circle is : {circleArea}{" "}
                          {convertedCircleUnit}
                          <sup>2</sup>
                        </p>
                      </Fragment>
                    ) : (
                      ""
                    )}
                  </Form>
                )}
              </Formik>
            </div>
            <div className="col-sm-12 col-md-4 mt-md-5 mt-3 text-center">
              <img alt="Calculating the Circle Area - Circle Area Calculator" src={circleimg} />
            </div>
          </div>
          <hr />
                    <div className="row">
            <div className="col-sm-12 col-md-7 calculatorStyle">
              <Formik
                initialValues={initialSectorValues}
                validationSchema={sectorSchema}
                onSubmit={onSubmitSector}
              >
                {(formik) => (
                  <Form>
                    <div className="mb-3">
                      <h3
                        className="p-3 mb-2 bg-success text-white"
                        style={{ fontSize: "16px", fontWeight: "bold" }}
                      >
                        Calculating the Sector Area - Sector Area Calculator
                      </h3>
                      <TextField
                        label="Radius"
                        name="radius"
                        value={formik.values.radius}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.radius && formik.errors.radius}
                        helperText={
                          formik.touched.radius && formik.errors.radius
                        }
                        sx={{
                          width: "160px",
                          ml: "10px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor: "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <Field
                        as={TextField}
                        select
                        name="radiusUnit"
                        value={formik.values.radiusUnit}
                        onChange={formik.handleChange}
                        size="small"
                        style={{
                          backgroundColor: "#f9f9f9",
                          pending: "5px",
                        }}
                      >
                        {Object.keys(conversionMap).map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Field>
                    </div>
                    <div
                      className="mb-3"
                      style={{ display: "flex", alignItems: "baseline" }}
                    >
                      <TextField
                        label="Angle"
                        name="angle"
                        value={formik.values.angle}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.angle && formik.errors.angle}
                        helperText={formik.touched.angle && formik.errors.angle}
                        sx={{
                          width: "160px",
                          ml: "10px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor: "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <p
                        style={{
                          borderRadius: "4px",
                          border: " 1px solid rgba(0, 0, 0, 0.23)",
                          backgroundColor: "#f9f9f9",
                          padding: "6.5px 18px 6.5px 15px",
                          userSelect: "none",
                        }}
                      >
                        ° degree
                      </p>
                    </div>
                    <div className="mb-3">
                      <Button
                        type="submit"
                        disabled={!formik.isValid}
                        color="success"
                        sx={{
                          backgroundColor: "rgb(25,135,84)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                      >
                        Calculte
                      </Button>
                      <Button
                        disabled={!formik.isValid}
                        sx={{
                          backgroundColor: "rgb(108,117,125)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                        onClick={() => {
                          setSectorArea(null);
                          formik.resetForm();
                        }}
                      >
                        Clear
                      </Button>
                    </div>
                    {sectorArea && sectorArea > 0 ? (
                      <Fragment>
                        <p className="p-3 mb-2 bg-success text-white">
                          The result: The area of the sector is: {sectorArea}{" "}
                          {convertedSectorUnit}
                          <sup>2</sup>
                        </p>
                      </Fragment>
                    ) : (
                      ""
                    )}
                  </Form>
                )}
              </Formik>
            </div>
            <div className="col-sm-12 col-md-4 mt-md-5 mt-3 text-center">
              <img alt="Calculating Sector Area - Sector Area Calculator" src={sectorimg} />
            </div>
          </div>
          <hr />
                    <div className="row">
          <div className="col-sm-12 col-md-7 calculatorStyle">
              <Formik
                initialValues={initialEllipseValues}
                validationSchema={ellipseSchema}
                onSubmit={onSubmitEllipse}
              >
                {(formik) => (
                  <Form>
                    <div className="mb-3">
                      <h3
                        className="p-3 mb-2 bg-success text-white"
                        style={{ fontSize: "16px", fontWeight: "bold" }}
                      >
                        Ellipse Area Calculation - Ellipse Area Calculator
                      </h3>
                      <TextField
                        label="Semi Major Axes"
                        name="semiMajorAxes"
                        value={formik.values.semiMajorAxes}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.semiMajorAxes && formik.errors.semiMajorAxes
                        }
                        helperText={
                          formik.touched.semiMajorAxes && formik.errors.semiMajorAxes
                        }
                        sx={{
                          width: "160px",
                          ml: "10px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor: "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <Field
                        as={TextField}
                        select
                        name="semiMajorAxesUnit"
                        value={formik.values.semiMajorAxesUnit}
                        onChange={formik.handleChange}
                        size="small"
                        style={{
                          backgroundColor: "#f9f9f9",
                          pending: "5px",
                        }}
                      >
                        {Object.keys(conversionMap).map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Field>
                    </div>
                    <div className="mb-3">
                      <TextField
                        label="Semi Minor Axes"
                        name="semiMinorAxes"
                        value={formik.values.semiMinorAxes}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.semiMinorAxes && formik.errors.semiMinorAxes
                        }
                        helperText={
                          formik.touched.semiMinorAxes && formik.errors.semiMinorAxes
                        }
                        sx={{
                          width: "160px",
                          ml: "10px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor: "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <Field
                        as={TextField}
                        select
                        name="semiMinorAxesUnit"
                        value={formik.values.semiMinorAxesUnit}
                        onChange={formik.handleChange}
                        size="small"
                        style={{
                          backgroundColor: "#f9f9f9",
                          pending: "5px",
                        }}
                      >
                        {Object.keys(conversionMap).map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Field>
                    </div>
                    <div className="mb-3">
                      <Button
                        type="submit"
                        disabled={!formik.isValid}
                        color="success"
                        sx={{
                          backgroundColor: "rgb(25,135,84)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                      >
                        Calculate
                      </Button>
                      <Button
                        disabled={!formik.isValid}
                        sx={{
                          backgroundColor: "rgb(108,117,125)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                        onClick={() => {
                          setEllipseArea(null);
                          formik.resetForm();
                        }}
                      >
                        Clear Result
                      </Button>
                    </div>
                    {ellipseArea && ellipseArea > 0 ? (
                      <Fragment>
                        <p className="p-3 mb-2 bg-success text-white">
                          Result: Ellipse area is: {ellipseArea} {convertedEllipseUnit}
                          <sup>2</sup>
                        </p>
                      </Fragment>
                    ) : (
                      ""
                    )}
                  </Form>
                )}
              </Formik>
            </div>
            <div className="col-sm-12 col-md-4 mt-md-5 mt-3 text-center">
              <img
                alt="Ellipse Area Calculation - Ellipse Area Calculator"
                src={ellipseimg}
              />
            </div>
          </div>
          <hr />
          <div className="row">
          <div className="col-sm-12 col-md-7 calculatorStyle">
              <Formik
                initialValues={initialParallelogramValues}
                validationSchema={parallelogramSchema}
                onSubmit={onSubmitParallelogram}
              >
                {(formik) => (
                  <Form>
                    <div className="mb-3">
                      <h3
                        className="p-3 mb-2 bg-success text-white"
                        style={{ fontSize: "16px", fontWeight: "bold" }}
                      >
                        Parallelogram Area Calculation - Parallelogram Area Calculator
                      </h3>
                      <TextField
                        label="Base"
                        name="base"
                        value={formik.values.base}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.base && formik.errors.base}
                        helperText={formik.touched.base && formik.errors.base}
                        sx={{
                          width: "160px",
                          ml: "10px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor: "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <Field
                        as={TextField}
                        select
                        name="baseUnit"
                        value={formik.values.baseUnit}
                        onChange={formik.handleChange}
                        size="small"
                        style={{
                          backgroundColor: "#f9f9f9",
                          pending: "5px",
                        }}
                      >
                        {Object.keys(conversionMap).map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Field>
                    </div>
                    <div className="mb-3">
                      <TextField
                        label="Height"
                        name="height"
                        value={formik.values.height}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.height && formik.errors.height}
                        helperText={formik.touched.height && formik.errors.height}
                        sx={{
                          width: "160px",
                          ml: "10px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor: "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <Field
                        as={TextField}
                        select
                        name="heightUnit"
                        value={formik.values.heightUnit}
                        onChange={formik.handleChange}
                        size="small"
                        style={{
                          backgroundColor: "#f9f9f9",
                          pending: "5px",
                        }}
                      >
                        {Object.keys(conversionMap).map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {unit}
                          </MenuItem>
                        ))}
                      </Field>
                    </div>
                    <div className="mb-3">
                      <Button
                        type="submit"
                        disabled={!formik.isValid}
                        color="success"
                        sx={{
                          backgroundColor: "rgb(25,135,84)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                      >
                        Calculate
                      </Button>
                      <Button
                        disabled={!formik.isValid}
                        sx={{
                          backgroundColor: "rgb(108,117,125)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                        onClick={() => {
                          setParallelogramArea(null);
                          formik.resetForm();
                        }}
                      >
                        Clear Result
                      </Button>
                    </div>
                    {parallelogramArea && parallelogramArea > 0 ? (
                      <Fragment>
                        <p className="p-3 mb-2 bg-success text-white">
                          Result: Parallelogram area is:{" "}
                          {parallelogramArea} {convertedParallelogramUnit}
                          <sup>2</sup>
                        </p>
                      </Fragment>
                    ) : (
                      ""
                    )}
                  </Form>
                )}
              </Formik>
            </div>
            <div className="col-sm-12 col-md-4 mt-md-5 mt-3 text-center">
              <img
                alt="Parallelogram Area Calculation - Parallelogram Area Calculator"
                src={parallelogramimg}
              />
            </div>
          </div>
          <hr />
          <div className="row">
            <RelatedLinks />
            <hr />
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList
            CalculatorsTypeId={CalculatorsType.mathCalculators}
          ></MainCalculatorsList>
          <br />
        </div>
      </div>
      <div className="col-12 col-md-8">
        <div className="info-sections-container">
          <div className="info-section">
            <div className="info-section-header">
              <u><h3 style={{ fontSize: '16px' }}>Area</h3></u>
            </div>
            <div className="info-section-body">
              <p>
                Area is a quantity that describes the extent of a two-dimensional shape or figure in a plane. It can be visualized as the amount of paint needed to cover a surface and is the two-dimensional counterpart to the length of a curve and the volume of a three-dimensional mass. The standard unit of measurement for area in the International System of Units (SI) is the square meter, or m<sup>2</sup>. Below are equations for some common and simple shapes, along with examples of how to calculate the area for each.
              </p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header">
              <u><h3 style={{ fontSize: '16px' }}>Rectangle Area Calculation</h3></u>
            </div>
            <div className="info-section-body">
              <p>
                A rectangle is a quadrilateral with four right angles. It is one of the simplest shapes, and calculating its area requires only knowing its length and width (or being able to measure them). A rectangle is defined as a quadrilateral with four edges and vertices. In the case of a rectangle, the length usually refers to the longest edge among the four edges, while the width refers to the shorter edge between the two. When the length and width are equal, the shape is a special case of the rectangle called a square. The equation to calculate the area of a rectangle is as follows:
              </p>
              <p>
                <strong>Area = Length × Width</strong>
              </p>
              <p>For example, if the length of the rectangle is 10 meters and the width is 5 meters:</p>
              <code>
                Area = 10 m × 5 m = 50 m<sup>2</sup>
              </code>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header">
              <u><h3 style={{ fontSize: '16px' }}>Triangle Area Calculation</h3></u>
            </div>
            <div className="info-section-body">
              <p>
                There are several equations to calculate the area of a triangle based on the available information. As mentioned in the above calculator, please use a triangle calculator to get more details and equations to calculate the area of a triangle, in addition to specifying the triangle's sides using any available information. In short, the equation used in the provided calculator is known as the "Heron's formula," named after Hero of Alexandria, a Greek mathematician and engineer considered by some as one of the greatest contributors in ancient times. The equation is as follows:
              </p>
              <p>
                <strong>Area = √s(s - x)(s - y)(s - z)</strong>
              </p>
              <p>
                Where:
              </p>
              <p>
                <strong>s = (x + y + z) / 2</strong>
              </p>
              <p>
                And
              </p>
              <p>
                <strong>x, y, z</strong> are the sides of the triangle
              </p>
              <p>For example, if you have a triangle with side lengths of 7 meters, 9 meters, and 12 meters:</p>
              <code>
                s = (7 m + 9 m + 12 m) / 2 = 14 m
              </code>
              <code>
                Area = √14 × (14 - 7) × (14 - 9) × (14 - 12) = 21 m<sup>2</sup>
              </code>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header">
              <u><h3 style={{ fontSize: '16px' }}>Circle Area Calculation</h3></u>
            </div>
            <div className="info-section-body">
              <p>
                A circle is a simple closed shape consisting of all points in a plane that are at a given distance from a specific central point. This distance from the center to any point on the circle is called the radius. More details about circles can be found on the circle calculator page, but to calculate the area, all you need to know is the radius and understand that values in the circle are related through the mathematical constant π. The equation to calculate the area of a circle is as follows:
              </p>
              <p>
                Area = π × (Radius)<sup>2</sup>
              </p>
              <p>For example, if you have a circle with a radius of 10 meters:</p>
              <code>
                Area = π × (10 m)<sup>2</sup> = 100π m<sup>2</sup>
              </code>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header">
              <u><h3 style={{ fontSize: '16px' }}>Sector Area Calculation</h3></u>
            </div>
            <div className="info-section-body">
              <p>
                A sector in a circle is essentially a portion enclosed by two radii and an arc. Using the diameter and the angle, the area of the sector can be calculated by multiplying the area of the circle by the known angle ratio to 360° or 2π radians, as shown in the following equation:
              </p>
              <p>
                Area = (θ / 360) × π × (Radius)² if the angle θ is in degrees
              </p>
              <p>
                Or
              </p>
              <p>
                Area = (θ / 2π) × π × (Radius)² if the angle θ is in radians
              </p>
              <p>
                Due to the placebo effect, everyone gets a third less pie, and the daughter reminisces about studying American history, learning about the Alamo, and depicting the popular folk hero Davy Crockett with his raccoon-skin cap.
              </p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header">
              <u><h3 style={{ fontSize: '16px' }}>Parallelogram Area Calculation</h3></u>
            </div>
            <div className="info-section-body">
              <p>
                A parallelogram is a simple quadrilateral containing two pairs of parallel sides, where the opposite sides and angles are equal in length and measure. The rectangle, rhombus, and square are all special cases of a parallelogram. Remember that the classification of the shape as "simple" means that the shape is not self-intersecting. A parallelogram can be divided into a right-angled triangle and a shape resembling a parallelogram. The equation to calculate the area of a parallelogram is similar to that used for a rectangle, but instead of length and width, a parallelogram uses the base and the height, where the height is the perpendicular distance between a pair of bases. Based on the figure below, the equation to calculate the area of a parallelogram is as follows:
              </p>
              <code>
                Area = Base × Height
              </code>
              <p>For example, if you have a parallelogram with a base of 15 meters and a height of 10 meters:</p>
              <code>
                Area = 15 m × 10 m = 150 m<sup>2</sup>
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default AreaCalculators;
